import React, { useState } from 'react';
import AccountFullDTO from 'features/accounts/dtos/AccountFullDTO';
import PageCard from 'features/shared/components/card/PageCard';
import AccountRestriction from 'features/accounts/components/AccountRestriction';
import NoContentWrapper from 'features/shared/components/noContentWrapper/NoContentWrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AccountRestrictionDTO from 'features/accounts/dtos/AccountRestrictionDTO';
import { trackPromise } from 'react-promise-tracker';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import IRepository from 'data/IRepository';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import { Accounts_DeleteRestriction, Accounts_InsertRestriction, Accounts_UpdateRestriction } from 'data/endpoints';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import AccountRestrictionDialog, { AccountRestrictionDialogMode } from 'features/accounts/components/accountRestrictionDialog/AccountRestrictionDialog';
import { useAccountRestrictionsData } from 'features/accounts/hooks/useAccountRestrictionsData';
import { AccountRestrictions_AccountRestrictionDialog } from 'features/shared/components/loaders/promiseLoadingAreas';
import { Nullable } from 'features/shared/types/nullable';
import PostResponse from 'data/PostResponse';
import IContentDialogService from 'features/shared/components/contentDialog/IContentDialogService';
import ContentDialogConfirm from 'features/shared/components/contentDialog/ContentDialogConfirm';
import CardHeaderButton from 'features/shared/components/card/CardHeaderButton';
import { useTranslation } from 'react-i18next';
import { getForAdminParam } from 'features/shared/helpers/queryParams';

interface Props {
  account: AccountFullDTO;
  accountModified: number;
  setAccountModified: React.Dispatch<React.SetStateAction<number>>;
  forAdminUse?: boolean;
}

const AccountRestrictionsSection = ({ account, accountModified, setAccountModified, forAdminUse = false }: Props) => {
  // translations
  const tLabels = useTranslation("accounts\\accounts").t;

  // DI
  const restrictionRepository = useInjection<IRepository<AccountFullDTO>>(IocTypes.Repository);
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService); 

  // state
  const [showRestrictionDialog, setShowRestrictionDialog] = useState(false);
  const [restrictionDialogMode, setRestrictionDialogMode] = useState<AccountRestrictionDialogMode>("Add");
  const [restrictionToUpadate, setRestrictionToUpadate] = useState<Nullable<AccountRestrictionDTO>>(null);

  // helpers
  const filterDataByRestrictions = <T,>(
    data: T[],
    existRestrictions: AccountRestrictionDTO[],
    filterProperty: string,
    updateRestricion: Nullable<AccountRestrictionDTO>
  ): T[] => {
    if (existRestrictions.length > 0) {
      let filtredExistRestricions = updateRestricion ? existRestrictions.filter(x => (x as any)[filterProperty] !== (updateRestricion as any)[filterProperty]) : existRestrictions;

      return data.filter((x) =>
        !filtredExistRestricions.some(
          (y) => (x as any)[filterProperty] === (y as any)[filterProperty]
        )
      );
    }
    return data;
  };

  const filteredContracts = () => filterDataByRestrictions(contracts, account.restrictions!, "placeOfDeliveryId", restrictionToUpadate);
  
  const filteredContractualAccounts = () => filterDataByRestrictions(contractualAccounts.result, account.restrictions!, "contractualAccountId", restrictionToUpadate);

  // fetch data
  const { contracts, contractualAccounts } = useAccountRestrictionsData(account.customerId!, showRestrictionDialog, forAdminUse);

  // handlers
  const deleteRestrictionHandler = async (restriction: AccountRestrictionDTO) => {
    let response = await trackPromise(
      restrictionRepository.delete({
        url: Accounts_DeleteRestriction,
        routeParams: getRouteParams("accountRestrictionId", restriction.accountRestrictionId!),
        params: getForAdminParam(forAdminUse)
      })
    );

    if (response.status == 204) {
      setAccountModified(accountModified + 1);
    }
  }

  const confirmDeleteRestrictionHandler = (restriction: AccountRestrictionDTO) => {
    dialogService.openDialog(tLabels("accountRestrictionsSection.deleteRestriction"), () => {
      return (
        <ContentDialogConfirm
          message={tLabels("accountRestrictionsSection.deleteRestrictionQuestion")}
          action={() => deleteRestrictionHandler(restriction)}
        />
      );
    });
  }

  const submitRestrictionDialogHandler = async (restriction: AccountRestrictionDTO, mode: AccountRestrictionDialogMode) => {
    restriction.accountId = account.accountId;
    let response: PostResponse<string>;
    
    switch (mode) {
      case "Add":
        response = await trackPromise(
          restrictionRepository.post<string>({
            url: Accounts_InsertRestriction,
            data: restriction,
            routeParams: getRouteParams("accountId", account.accountId!),
            params: getForAdminParam(forAdminUse)
          }),
          AccountRestrictions_AccountRestrictionDialog
        );
        break;
      case "Update":
        response = await trackPromise(
          restrictionRepository.put<string>({
            url: Accounts_UpdateRestriction,
            data: restriction,
            routeParams: getRouteParams("accountRestrictionId", restrictionToUpadate?.accountRestrictionId!),
            params: getForAdminParam(forAdminUse)
          }),
          AccountRestrictions_AccountRestrictionDialog
        );
        break;
      default:
        response = { status: 200 };
        break;
    }

    if ([200, 204].includes(response.status)) {
      setShowRestrictionDialog(false);
      setAccountModified(accountModified + 1);
    }
  }

  const showAddRestrictionDialogHandler = () => {
    setRestrictionToUpadate(null);
    setRestrictionDialogMode("Add");
    setShowRestrictionDialog(true);
  }

  const showUpdateRestrictionDialogHandler = (restriction: AccountRestrictionDTO) => {
    setRestrictionToUpadate(restriction);
    setRestrictionDialogMode("Update");
    setShowRestrictionDialog(true);
  }

  // sub-renders
  const renderRestrictions = () => {
    return account.restrictions?.map((item) =>
      <div className="py-1" key={item.accountRestrictionId}>
        <AccountRestriction 
          restriction={item} 
          onDeleteClick={confirmDeleteRestrictionHandler}
          onEditClick={showUpdateRestrictionDialogHandler}
        />
      </div>
    );
  }
  
  const renderActionBtns = () => {
    let elem = (
        <CardHeaderButton
          type="button"
          title={tLabels("accountRestrictionsSection.addRestriction")}
          onClick={showAddRestrictionDialogHandler}
          key={1}
        >
          <FontAwesomeIcon icon="plus" />
        </CardHeaderButton>
    );

    return [elem];
  };

  // render
  return (
    <PageCard 
      title={tLabels("accountRestrictionsSection.cardTitle")}
      headerActionBtns={renderActionBtns}
    >
      <NoContentWrapper
        data={account.restrictions}
        loadStatus="AfterFirstLoad"
        noContentRender={() => <div className="text-muted">{tLabels("accountRestrictionsSection.noRestrictionAdded")}</div>}
      >
        {renderRestrictions()}  
      </NoContentWrapper>
      
      <AccountRestrictionDialog 
        show={showRestrictionDialog}
        setShow={setShowRestrictionDialog}
        mode={restrictionDialogMode}
        contracts={filteredContracts()}
        contractualAccounts={filteredContractualAccounts()}
        selectedRestriction={restrictionToUpadate}
        onSubmit={submitRestrictionDialogHandler}
      />
    </PageCard>
  )
}

export default AccountRestrictionsSection;
